import { ref } from 'vue'
import type { TranslationKey } from '../types/TranslationKey'

// Strict rule (what you logically enforce at runtime)
export type Rule =
  | { type: 'required'; messageKey: TranslationKey }
  | { type: 'minLength'; value: number; messageKey: TranslationKey }
  | { type: 'maxLength'; value: number; messageKey: TranslationKey }
  | { type: 'pattern'; value: RegExp; messageKey: TranslationKey }

// Loose rule input (what schema builders are allowed to pass)
export type LooseRule =
  | { type: 'required'; messageKey: TranslationKey }
  | { type: 'minLength'; value?: number; messageKey: TranslationKey }
  | { type: 'maxLength'; value?: number; messageKey: TranslationKey }
  | { type: 'pattern'; value?: RegExp; messageKey: TranslationKey }

type Schema<T> = Partial<Record<keyof T, readonly LooseRule[]>>

export function useValidation<T extends Record<string | number | symbol, any>>(
  model: T,
  schema: Schema<T>
) {
  const errors = ref<Partial<Record<keyof T, TranslationKey>>>({})
  const touched = ref<Partial<Record<keyof T, boolean>>>({})

  function runRules<K extends keyof T>(key: K, value: T[K]): TranslationKey | null {
    const rules = (schema[key] ?? []) as readonly LooseRule[]

    for (const r of rules) {
      // required
      if (r.type === 'required') {
        if (!value && value !== 0) return r.messageKey
        continue
      }

      // min (skip if value is undefined)
      if (r.type === 'minLength') {
        if (typeof r.value === 'number') {
          if (String(value ?? '').length < r.value) return r.messageKey
        }
        continue
      }

      // max (skip if value is undefined)
      if (r.type === 'maxLength') {
        if (typeof r.value === 'number') {
          if (String(value ?? '').length > r.value) return r.messageKey
        }
        continue
      }

      // pattern (skip if pattern is undefined)
      if (r.type === 'pattern') {
        if (r.value instanceof RegExp) {
          if (value && !r.value.test(String(value))) return r.messageKey
        }
        continue
      }
    }

    return null
  }

  function validateField<K extends keyof T>(key: K) {
    const msg = runRules(key, model[key])
    if (msg) errors.value[key] = msg
    else delete errors.value[key]
  }

  function validateAll(): boolean {
    ;(Object.keys(schema) as (keyof T)[]).forEach(k => validateField(k))
    return Object.keys(errors.value).length === 0
  }

  function markTouched<K extends keyof T>(key: K) {
    touched.value[key] = true
  }

  function markTouchedAndValidate<K extends keyof T>(key: K) {
    markTouched(key)
    validateField(key)
  }

  function resetValidation<K extends keyof T>(...keys: K[]) {
    if (keys.length === 0) {
      // replace objects to trigger reactivity in one go
      errors.value = {} as Partial<Record<keyof T, TranslationKey>>
      touched.value = {} as Partial<Record<keyof T, boolean>>
      return
    }
    // selective reset
    keys.forEach(k => {
      delete errors.value[k]
      delete touched.value[k]
    })
  }

  return {
    errors,
    touched,
    validateField,
    validateAll,
    markTouched,
    markTouchedAndValidate,
    resetValidation,
  }
}
